/**
 * OverflowChips — a table-cell chip list that STAYS COMPACT: it shows the first
 * few chips inline and folds the rest behind a "+N" Popover trigger, so a row
 * with many Assets / Use Cases / Topics / Tags can't stretch the column.
 *
 * Why a Popover (not a Modal like the Activities cell): these are short labels a
 * user wants to glance at in place — a modal would be too heavy. The Activities
 * cell opens a Modal because its content is a rich, tall timeline; chips are not.
 *
 * Each chip supports three modes so the same component serves plain lists today
 * and linked lists once asset/uc/topic detail routes land:
 *   - plain  : { label }                      → <span>
 *   - link   : { label, href, external }      → <a> (external opens a new tab)
 *   - action : { label, onClick }             → <button> (e.g. in-app navigate)
 *
 * Empty list → a single em-dash, matching the other cells.
 */

import { Popover } from '@amzn/awsui-components-react';
import './OverflowChips.css';

export interface OverflowChipItem {
  /** Stable React key (also the a11y anchor); usually the id. */
  key: string;
  /** Display text. */
  label: string;
  /** Tooltip; defaults to label (useful when the chip ellipsizes). */
  title?: string;
  /** Link target — renders the chip as an anchor. */
  href?: string;
  /** When href is set, open in a new tab (external deep-link). */
  external?: boolean;
  /** Click handler — renders the chip as a button (e.g. in-app navigation). */
  onClick?: () => void;
}

interface OverflowChipsProps {
  items: OverflowChipItem[];
  /** How many chips to show inline before folding into "+N". Default 2. */
  visibleCount?: number;
  /** Rendered when items is empty. Default em-dash. */
  emptyText?: string;
  /** aria-label for the chip group (e.g. "Assets") — REQUIRED so the group is
   *  always announced with its purpose (a bare chip list is meaningless to a
   *  screen reader). */
  ariaLabel: string;
}

function Chip({ item }: { item: OverflowChipItem }) {
  const title = item.title ?? item.label;
  if (item.href) {
    return (
      <a
        className="ivt-chip ivt-chip--interactive"
        href={item.href}
        title={title}
        {...(item.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {item.label}
      </a>
    );
  }
  if (item.onClick) {
    return (
      <button type="button" className="ivt-chip ivt-chip--interactive" title={title} onClick={item.onClick}>
        {item.label}
      </button>
    );
  }
  return (
    <span className="ivt-chip" title={title}>
      {item.label}
    </span>
  );
}

export default function OverflowChips({
  items,
  visibleCount = 2,
  emptyText = '—',
  ariaLabel,
}: OverflowChipsProps) {
  if (items.length === 0) return <>{emptyText}</>;

  const visible = items.slice(0, visibleCount);
  const overflow = items.slice(visibleCount);

  return (
    // role="group" so screen readers actually honor aria-label on this div (a bare
    // <div aria-label> is ignored by most AT). (AutoSDE CR-286035286 r1.)
    <div className="ivt-chips" role="group" aria-label={ariaLabel}>
      {visible.map((item) => (
        <Chip key={item.key} item={item} />
      ))}
      {overflow.length > 0 && (
        <Popover
          position="top"
          size="large"
          triggerType="custom"
          dismissButton={false}
          renderWithPortal
          // No aria-label here: the outer .ivt-chips group already carries it;
          // labeling the portal content too would announce the group name twice.
          // Show the FULL list (all items, not just the folded remainder) so the
          // popover is the complete view; chips render untruncated here (see CSS).
          content={
            <div className="ivt-chips ivt-chips--popover">
              {items.map((item) => (
                <Chip key={item.key} item={item} />
              ))}
            </div>
          }
        >
          {/* "+N" badge = count of chips hidden inline; clicking opens the FULL
              list. aria-label in Chinese to match the app's zh UI. The title
              lists every label so they're discoverable on hover (the Popover
              keeps them out of the DOM until opened, so Ctrl+F can't find them —
              acceptable for a compact cell; the tooltip is the cheap mitigation). */}
          <button
            type="button"
            className="ivt-chip ivt-chip--more"
            aria-label={`展开全部 ${items.length} 项 ${ariaLabel}`}
            title={items.map((o) => o.label).join(', ')}
          >
            +{overflow.length}
          </button>
        </Popover>
      )}
    </div>
  );
}
